'use client';

import type {
  ChangeEvent,
  MouseEvent,
  ReactNode,
} from 'react';
import { useRef } from 'react';
import {
  AddIcon,
  CloseIcon,
} from 'tdesign-icons-react';
import type {
  ImageViewerProps,
  UploadFile,
  UploadProps,
} from 'tdesign-react';
import {
  Form,
  ImageViewer,
  MessagePlugin,
  Upload,
} from 'tdesign-react';
import {
  getPreviewUrlsEn,
  postUploadFileEn,
  ruleRequired,
} from '@ssv-website/hooks';

const { FormItem } = Form;

const AVATAR_ACCEPT = '.jpg,.jpeg,.png';
const AVATAR_UPLOAD_MODULE = 'team';
const AVATAR_SIZE_LIMIT_MB = 2;
const AVATAR_TYPE_LIST = ['image/jpeg', 'image/jpg', 'image/png'];
const AVATAR_EXT_LIST = ['jpg', 'jpeg', 'png'];
const AVATAR_UPLOAD_ERROR_MESSAGE =
  'Profile picture upload failed. Please try again.';
const AVATAR_FILE_TYPE_ERROR_MESSAGE =
  'Invalid file type. Please choose a .jpg, .jpeg, or .png image.';
const AVATAR_PLACEHOLDER = 'Click to upload image';
const AVATAR_UPLOADING_TEXT = 'Uploading';
const AVATAR_SIZE_LIMIT_MESSAGE = 'Maximum file size: {sizeLimit} MB';

type AvatarUploadFieldProps = {
  label: ReactNode;
  name: string | (string | number)[];
  requiredRuleContent: string;
  prompt: ReactNode;
  promptClassName?: string;
  uploadClassName?: string;
  uploadWrapperClassName?: string;
  onUploadStateChange?: () => void;
  disabled?: boolean;
  draggable?: boolean;
  placeholder?: string;
  useCustomMobileUploadUi?: boolean;
};

type AvatarUploadInputProps = {
  accept?: string;
  className?: string;
  disabled?: boolean;
  onUploadStateChange?: () => void;
  placeholder?: string;
  value?: UploadFile[];
  onChange?: (value: UploadFile[]) => void;
};

const getFirstUploadFile = (value: unknown) => {
  if (!Array.isArray(value) || value.length === 0) {
    return undefined;
  }

  const [file] = value;
  if (!file || typeof file !== 'object') {
    return undefined;
  }

  return file as UploadFile;
};

const normalizeUploadFiles = (value: unknown) => {
  if (!Array.isArray(value)) {
    return [] as UploadFile[];
  }

  return value.filter(Boolean) as UploadFile[];
};

const getUploadResponse = (file: UploadFile | undefined) => {
  if (!file?.response || typeof file.response !== 'object') {
    return undefined;
  }

  return file.response as Record<string, unknown>;
};

export const getUploadPreviewUrl = (value: unknown) => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (Array.isArray(value) && value.length > 0) {
    const [firstItem] = value;
    if (typeof firstItem === 'string') {
      return firstItem;
    }
    if (firstItem && typeof firstItem === 'object') {
      const file = firstItem as UploadFile;
      const response = getUploadResponse(file);
      return String(file.url ?? response?.url ?? response?.value ?? '');
    }
    return '';
  }
  if (
    typeof value === 'object' &&
    'url' in (value as Record<string, unknown>)
  ) {
    return String((value as Record<string, unknown>).url ?? '');
  }
  return '';
};

export const getUploadSubmitUrl = (value: unknown) => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (Array.isArray(value) && value.length > 0) {
    const [firstItem] = value;
    if (typeof firstItem === 'string') {
      return firstItem;
    }
    if (firstItem && typeof firstItem === 'object') {
      const file = firstItem as UploadFile;
      const response = getUploadResponse(file);
      return String(response?.value ?? file.url ?? response?.url ?? '');
    }
    return '';
  }
  if (
    typeof value === 'object' &&
    'url' in (value as Record<string, unknown>)
  ) {
    return String((value as Record<string, unknown>).url ?? '');
  }
  return '';
};

export const isAvatarUploadReady = (value: unknown) => {
  const file = getFirstUploadFile(value);

  if (!file) {
    return false;
  }

  return file.status === 'success' && Boolean(getUploadPreviewUrl(value));
};

const isValidAvatarFileType = (file: UploadFile) => {
  const fileType = String(file.type ?? '').toLowerCase();
  if (fileType && AVATAR_TYPE_LIST.includes(fileType)) {
    return true;
  }

  const ext = String(file.name ?? '').split('.').pop()?.toLowerCase();
  return Boolean(ext && AVATAR_EXT_LIST.includes(ext));
};

const syncUploadState = (callback?: () => void) => {
  if (!callback) return;
  window.setTimeout(() => {
    callback();
  }, 0);
};

const AVATAR_SIZE_LIMIT_BYTES = AVATAR_SIZE_LIMIT_MB * 1024 * 1024;
const AVATAR_SIZE_LIMIT_MESSAGE_TEXT = AVATAR_SIZE_LIMIT_MESSAGE.replace(
  '{sizeLimit}',
  String(AVATAR_SIZE_LIMIT_MB)
);

const normalizeFileName = (fileName: string): string => {
  const dotIndex = fileName.lastIndexOf('.');
  if (dotIndex === -1 || dotIndex === 0) return fileName;
  return fileName.slice(0, dotIndex) + fileName.slice(dotIndex).toLowerCase();
};

const createPendingUploadFile = (file: File) =>
  ({
    name: normalizeFileName(file.name),
    size: file.size,
    type: file.type,
    raw: file,
    status: 'progress',
    percent: 0,
  }) as UploadFile;

const uploadAvatarRequestMethod = async (file: UploadFile | UploadFile[]) => {
  const currentFile = Array.isArray(file) ? file[0] : file;

  if (!currentFile?.raw) {
    return {
      status: 'fail' as const,
      error: AVATAR_UPLOAD_ERROR_MESSAGE,
      response: {},
    };
  }

  const contentType = currentFile.type || 'application/octet-stream';
  const urlencoded = new URLSearchParams();
  urlencoded.append('module', AVATAR_UPLOAD_MODULE);
  urlencoded.append('filename', String(currentFile.name ?? 'avatar.png'));
  urlencoded.append('contentType', contentType);
  urlencoded.append('size', String(currentFile.size ?? 0));

  try {
    const itemRes = await postUploadFileEn(urlencoded);
    // 使用原生 fetch 直接上传到 COS，避免 ClientApi.put 自动添加 credentials: 'include'
    // 导致跨域请求携带 cookie 触发 CORS 校验失败（403）
    const uploadRes = await fetch(itemRes.url, {
      method: 'PUT',
      body: currentFile.raw,
      headers: {
        'Content-Type': contentType,
        'x-cos-acl': 'private',
      },
      // 不携带 cookie，避免 COS 跨域校验拒绝
      credentials: 'omit',
    });

    // COS 上传成功返回 200 或 204
    if (uploadRes.status !== 200 && uploadRes.status !== 204) {
      return {
        status: 'fail' as const,
        error: AVATAR_UPLOAD_ERROR_MESSAGE,
        response: {},
      };
    }

    const [originUrl] = String(itemRes.url).split('?');
    const { urls = [] } = await getPreviewUrlsEn({ paths: [itemRes.path] });

    const previewUrl = urls[0] ?? originUrl;
    const data = {
      url: previewUrl,
      path: itemRes.path,
      name: currentFile.name,
      size: currentFile.size,
      Name: currentFile.name,
      Size: currentFile.size,
    };

    return {
      status: 'success' as const,
      response: {
        ...data,
        data,
        url: previewUrl,
        value: itemRes.path,
        path: itemRes.path,
      },
    };
  } catch (error) {
    return {
      status: 'fail' as const,
      error: AVATAR_UPLOAD_ERROR_MESSAGE,
      response: {},
    };
  }
};

const createAvatarUploadProps = (
  onUploadStateChange?: () => void,
  disabled = false,
  draggable = false,
  placeholder = AVATAR_PLACEHOLDER
): UploadProps => ({
  theme: 'image',
  accept: AVATAR_ACCEPT,
  autoUpload: true,
  max: 1,
  multiple: false,
  draggable,
  disabled,
  allowUploadDuplicateFile: true,
  showUploadProgress: false,
  showImageFileName: false,
  locale: { triggerUploadText: { fileInput: placeholder } },
  sizeLimit: {
    size: AVATAR_SIZE_LIMIT_MB,
    unit: 'MB',
    message: AVATAR_SIZE_LIMIT_MESSAGE,
  },
  requestMethod: uploadAvatarRequestMethod,
  beforeUpload: (file) => {
    if (isValidAvatarFileType(file)) {
      return true;
    }

    MessagePlugin.error({
      content: AVATAR_FILE_TYPE_ERROR_MESSAGE,
      duration: 2000,
    });
    syncUploadState(onUploadStateChange);
    return false;
  },
  onSuccess: () => {
    syncUploadState(onUploadStateChange);
  },
  onRemove: () => {
    syncUploadState(onUploadStateChange);
  },
  onFail: () => {
    MessagePlugin.error({
      content: AVATAR_UPLOAD_ERROR_MESSAGE,
      duration: 2000,
    });
    syncUploadState(onUploadStateChange);
  },
  onValidate: () => {
    syncUploadState(onUploadStateChange);
  },
});

function MobileAvatarUploadInput({
  accept = AVATAR_ACCEPT,
  className,
  disabled = false,
  onUploadStateChange,
  placeholder = AVATAR_PLACEHOLDER,
  value,
  onChange,
}: AvatarUploadInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const files = normalizeUploadFiles(value);
  const [currentFile] = files;
  const previewUrl = getUploadPreviewUrl(files);
  const isUploading = Boolean(currentFile) && currentFile.status !== 'success';

  const openFileDialog = () => {
    if (disabled || isUploading) {
      return;
    }

    inputRef.current?.click();
  };

  const clearInputValue = () => {
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const handleRemove = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    clearInputValue();
    onChange?.([]);
    syncUploadState(onUploadStateChange);
  };

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    clearInputValue();

    if (!selectedFile) {
      return;
    }

    const pendingUploadFile = createPendingUploadFile(selectedFile);

    if (!isValidAvatarFileType(pendingUploadFile)) {
      MessagePlugin.error({
        content: AVATAR_FILE_TYPE_ERROR_MESSAGE,
        duration: 2000,
      });
      onChange?.([]);
      syncUploadState(onUploadStateChange);
      return;
    }

    if (selectedFile.size > AVATAR_SIZE_LIMIT_BYTES) {
      MessagePlugin.error({
        content: AVATAR_SIZE_LIMIT_MESSAGE_TEXT,
        duration: 2000,
      });
      onChange?.([]);
      syncUploadState(onUploadStateChange);
      return;
    }

    onChange?.([pendingUploadFile]);
    syncUploadState(onUploadStateChange);

    const result = await uploadAvatarRequestMethod(pendingUploadFile);
    if (result.status === 'success') {
      const successFile = {
        ...pendingUploadFile,
        status: 'success',
        url: String(result.response?.url ?? result.response?.value ?? ''),
        response: result.response,
      } as UploadFile;

      onChange?.([successFile]);
      syncUploadState(onUploadStateChange);
      return;
    }

    MessagePlugin.error({
      content: result.error ?? AVATAR_UPLOAD_ERROR_MESSAGE,
      duration: 2000,
    });
    onChange?.([]);
    syncUploadState(onUploadStateChange);
  };

  const previewTrigger: ImageViewerProps['trigger'] = ({ open }) => (
    <button
      type="button"
      className="signup-2026-mobile-avatar-card signup-2026-mobile-avatar-preview"
      onClick={open}
    >
      <img
        src={previewUrl}
        alt=""
        className="signup-2026-mobile-avatar-image"
      />
    </button>
  );

  return (
    <div className={className}>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        disabled={disabled || isUploading}
        onChange={handleFileChange}
      />
      {previewUrl ? (
        <div className="signup-2026-mobile-avatar-preview-wrapper">
          <ImageViewer
            images={[previewUrl]}
            trigger={previewTrigger}
          />
          {!disabled ? (
            <button
              type="button"
              aria-label="Delete profile picture"
              className="signup-2026-mobile-avatar-remove"
              onClick={handleRemove}
            >
              <CloseIcon size={14} />
            </button>
          ) : null}
        </div>
      ) : isUploading ? (
        <div
          className="signup-2026-mobile-avatar-card signup-2026-mobile-avatar-loading"
          aria-live="polite"
        >
          <span className="signup-2026-mobile-avatar-spinner" />
          <span className="signup-2026-mobile-avatar-loading-text">
            {AVATAR_UPLOADING_TEXT}
          </span>
        </div>
      ) : (
        <button
          type="button"
          className="signup-2026-mobile-avatar-card signup-2026-mobile-avatar-trigger"
          disabled={disabled}
          onClick={openFileDialog}
        >
          <AddIcon
            size={32}
            className="signup-2026-mobile-avatar-add-icon"
          />
          <span className="signup-2026-mobile-avatar-trigger-text">
            {placeholder}
          </span>
        </button>
      )}
    </div>
  );
}

export function AvatarUploadField({
  label,
  name,
  requiredRuleContent,
  prompt,
  promptClassName,
  uploadClassName,
  uploadWrapperClassName,
  onUploadStateChange,
  disabled = false,
  draggable = false,
  placeholder,
  useCustomMobileUploadUi = false,
}: AvatarUploadFieldProps) {
  const customMobileUploadClassName = [
    uploadWrapperClassName,
    uploadClassName,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <FormItem
        label={label}
        name={name}
        rules={requiredRuleContent ? [ruleRequired({ content: requiredRuleContent })] : []}
      >
        {useCustomMobileUploadUi ? (
          <MobileAvatarUploadInput
            className={customMobileUploadClassName}
            disabled={disabled}
            onUploadStateChange={onUploadStateChange}
            placeholder={placeholder}
          />
        ) : (
          <Upload
            className={customMobileUploadClassName}
            {...createAvatarUploadProps(
              onUploadStateChange,
              disabled,
              draggable,
              placeholder
            )}
          />
        )}
      </FormItem>
      <p className={promptClassName}>{prompt}</p>
    </>
  );
}
